import { useState, useMemo } from 'react'
import { X, MapPin, Clock, Star, Copy, ChevronDown } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  Button,
} from '@/components/ui'
import { MAPBOX_TOKEN } from '@/config/mapbox'
import type { RestaurantInfo, RestaurantHours, MenuLocation } from '@/hooks/useMenuData'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'
import { getEffectiveSchedule } from '@/utils/effectiveSchedule'
import { getStoreStatus } from '@/utils/storeHours'
import {
  buildViewerRangeLabel,
  formatWallTime,
  getEffectiveBusinessTimeZone,
  getTimeZoneAbbreviation,
  getViewerTimeZone,
} from '@/utils/timezone'

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const

/**
 * Build a formatted hours list from API data for display.
 */
function buildHoursDisplay(
  apiHours: RestaurantHours[],
  businessTimeZone: string,
  viewerTimeZone: string
): Array<{ day: string; businessHours: string; viewerHours: string | null }> {
  const businessAbbr = getTimeZoneAbbreviation(new Date(), businessTimeZone)

  // Sort by day_of_week to ensure Monday-Sunday order
  const sorted = [...apiHours].sort((a, b) => a.day_of_week - b.day_of_week)
  return sorted.map((entry) => ({
    day: DAY_NAMES[entry.day_of_week] || `Day ${entry.day_of_week}`,
    businessHours: entry.is_closed
      ? 'Closed'
      : entry.is_24_hours
        ? 'Open 24 hours'
        : entry.open_time && entry.close_time
          ? `${formatWallTime(entry.open_time)} - ${formatWallTime(entry.close_time)} ${businessAbbr}`
          : 'Closed',
    viewerHours: entry.is_closed || entry.is_24_hours || !entry.open_time || !entry.close_time
      ? null
      : buildViewerRangeLabel(
          entry.day_of_week,
          entry.open_time,
          entry.close_time,
          businessTimeZone,
          viewerTimeZone
        ),
  }))
}

interface RestaurantInfoDialogProps {
  isOpen: boolean
  onClose: () => void
  restaurant: RestaurantInfo | null
}

export function RestaurantInfoDialog({
  isOpen,
  onClose,
  restaurant,
}: RestaurantInfoDialogProps) {
  const [isHoursExpanded, setIsHoursExpanded] = useState(false)
  const { location: selectedLocation, address: locationAddress } = useSelectedLocation()

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress)
  }

  const fullAddress = locationAddress

  // Read primary color from CSS variable for Mapbox static pin
  const pinHex = useMemo(() => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
    const [h, s, l] = raw.split(/\s+/).map(parseFloat)
    const sf = (s || 0) / 100, lf = (l || 0) / 100
    const a = sf * Math.min(lf, 1 - lf)
    const f = (n: number) => {
      const k = (n + (h || 0) / 30) % 12
      return Math.round(255 * (lf - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)))
        .toString(16).padStart(2, '0')
    }
    return `${f(0)}${f(8)}${f(4)}`
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const mapLat = selectedLocation?.latitude
  const mapLng = selectedLocation?.longitude
  const hasCoordinates = mapLat != null && mapLng != null
  const mapUrl = hasCoordinates
    ? `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s+${pinHex}(${mapLng},${mapLat})/${mapLng},${mapLat},14,0/600x300@2x?access_token=${MAPBOX_TOKEN}`
    : null

  const cuisineTags = restaurant?.cuisine_tags?.join(' \u00B7 ')

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md w-full max-h-[90vh] flex flex-col !p-0 gap-0 overflow-hidden" hideCloseButton>
        <DialogTitle className="sr-only">
          {restaurant?.title || 'Restaurant information'}
        </DialogTitle>
        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide overscroll-contain">
          {/* Floating close — sticky, zero-height so map starts at top */}
          <div className="sticky top-0 z-20 pointer-events-none h-0 overflow-visible">
            <div className="flex justify-end p-3">
              <DialogClose className="pointer-events-auto w-10 h-10 rounded-full bg-black/30 dark:bg-white/20 backdrop-blur-xl text-white shadow-lg flex items-center justify-center cursor-pointer hover:bg-black/50 dark:hover:bg-white/30 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </div>

          {/* Hero map with cinematic gradient */}
          <div className="relative h-48 bg-muted overflow-hidden">
            {mapUrl && (
              <img
                src={mapUrl}
                alt="Restaurant location map"
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          </div>

          {/* Content — emerges from gradient */}
          <div className="relative px-6 pb-6 -mt-8 pt-4">
            {/* Restaurant Name & Categories */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2">
                {restaurant?.title || 'Restaurant'}
              </h2>
              {cuisineTags && (
                <p className="text-sm text-muted-foreground">{cuisineTags}</p>
              )}
            </div>

            {/* Address */}
            <div className="flex items-start gap-3 pb-6 border-b border-border">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm">{fullAddress}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 flex-shrink-0 rounded-full"
                onClick={handleCopyAddress}
                title="Copy address"
                aria-label="Copy restaurant address"
              >
                <Copy className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>

            {/* Hours */}
            <HoursSection
              restaurant={restaurant}
              selectedLocation={selectedLocation}
              isExpanded={isHoursExpanded}
              onToggle={() => setIsHoursExpanded(!isHoursExpanded)}
            />

            {/* Rating — only shown when API provides data */}
            {restaurant?.rating && (
              <div className="flex items-center gap-3 pt-6">
                <Star className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <p className="text-sm">
                  {restaurant.rating}{restaurant.review_count ? ` (${restaurant.review_count} ratings)` : ''}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Hours section — uses API hours when available, falls back to hardcoded config.
 * Also checks hours_exceptions for today's date overrides.
 */
function HoursSection({
  restaurant,
  selectedLocation,
  isExpanded,
  onToggle,
}: {
  restaurant: RestaurantInfo | null
  selectedLocation: MenuLocation | null
  isExpanded: boolean
  onToggle: () => void
}) {
  const effectiveSchedule = getEffectiveSchedule(selectedLocation, restaurant)
  const apiHours = effectiveSchedule.hours
  const businessTimeZone = getEffectiveBusinessTimeZone(effectiveSchedule.timeZone, undefined)
  const viewerTimeZone = getViewerTimeZone()
  const businessAbbr = getTimeZoneAbbreviation(new Date(), businessTimeZone)
  const hasApiHours = apiHours && apiHours.length > 0

  // Determine the "Open until" / "Closed" status text for the header
  const storeStatus = useMemo(
    () => getStoreStatus(apiHours, {
      hoursExceptions: effectiveSchedule.hoursExceptions,
      businessTimeZone,
    }),
    [apiHours, businessTimeZone, effectiveSchedule.hoursExceptions]
  )

  // Build the expanded hours rows (only from API data — no hardcoded fallback)
  const hoursRows = useMemo(() => {
    if (!hasApiHours) return null
    return buildHoursDisplay(apiHours!, businessTimeZone, viewerTimeZone)
  }, [apiHours, businessTimeZone, hasApiHours, viewerTimeZone])

  return (
    <div className="py-6 border-b border-border">
      <Button
        variant="ghost"
        className="w-full flex items-start gap-3 h-auto text-left justify-start p-0 hover:bg-transparent"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <Clock className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">{hasApiHours ? storeStatus.businessLabel : 'See hours'}</p>
          {hasApiHours && storeStatus.viewerLabel && (
            <p className="text-xs text-muted-foreground mt-1">{storeStatus.viewerLabel}</p>
          )}
        </div>
        <ChevronDown
          className={`h-5 w-5 text-muted-foreground transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </Button>

      {isExpanded && (
        <div className="mt-4 ml-8 space-y-2 text-sm">
          <p className="text-xs text-muted-foreground pb-1">
            Times shown in restaurant local time ({businessAbbr}).
            {businessTimeZone !== viewerTimeZone ? ' Viewer-local conversions are shown below.' : ''}
          </p>
          {hoursRows ? (
            hoursRows.map(({ day, businessHours, viewerHours }) => (
              <div key={day} className="space-y-1">
                <div className="flex justify-between gap-4">
                  <span className="capitalize text-muted-foreground">{day}</span>
                  <span className="text-right">{businessHours}</span>
                </div>
                {viewerHours && (
                  <div className="flex justify-between gap-4 text-xs text-muted-foreground">
                    <span>Your time</span>
                    <span className="text-right">{viewerHours}</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-muted-foreground">Hours not available</p>
          )}
        </div>
      )}
    </div>
  )
}
